import React, {
  useEffect,
  useMemo,
  useState,
} from "react";

import { useNavigate } from "react-router-dom";

import "./SupplierStatements.css";

import supplierPortalService from "../../services/supplierPortalService";

const monthNames = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];

const getMonthName = (month) => {
  return monthNames[Number(month) - 1] || "";
};

const getStatementNumber = (statement) => {
  return `ST-${statement.statement_year}-${String(
    statement.statement_month
  ).padStart(2, "0")}`;
};

const getInitial = (name) => {
  return (
    name?.trim()?.charAt(0)?.toUpperCase() || "S"
  );
};

const SupplierStatements = () => {
  const navigate = useNavigate();

  const [supplier, setSupplier] = useState(null);
  const [statements, setStatements] = useState([]);

  const [year, setYear] = useState("All");
  const [status, setStatus] = useState("All");
  const [search, setSearch] = useState("");

  const [loading, setLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState("");

  const [profileOpen, setProfileOpen] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(false);

  useEffect(() => {
    let mounted = true;

    const loadStatements = async () => {
      try {
        setLoading(true);
        setErrorMessage("");

        if (!supplierPortalService.isLoggedIn()) {
          navigate("/supplier/login");
          return;
        }

        const [
          profileResponse,
          statementsResponse,
        ] = await Promise.all([
          supplierPortalService.getMe(),
          supplierPortalService.getStatements(),
        ]);

        if (!mounted) {
          return;
        }

        const profile =
          profileResponse?.supplier ||
          profileResponse ||
          null;

        const statementList =
          statementsResponse?.statements ||
          statementsResponse?.data ||
          [];

        setSupplier(profile);

        setStatements(
          Array.isArray(statementList)
            ? statementList
            : []
        );
      } catch (error) {
        console.error(
          "Supplier statements error:",
          error
        );

        if (!mounted) {
          return;
        }

        if (!supplierPortalService.isLoggedIn()) {
          navigate("/supplier/login");
          return;
        }

        setErrorMessage(
          error?.message ||
            "Unable to load supplier statements."
        );
      } finally {
        if (mounted) {
          setLoading(false);
        }
      }
    };

    loadStatements();

    return () => {
      mounted = false;
    };
  }, [navigate]);

  const years = useMemo(() => {
    const uniqueYears = [
      ...new Set(
        statements
          .map((item) => item.statement_year)
          .filter(
            (itemYear) =>
              itemYear !== null &&
              itemYear !== undefined
          )
      ),
    ];

    return uniqueYears.sort(
      (a, b) => Number(b) - Number(a)
    );
  }, [statements]);

  const filteredStatements = useMemo(() => {
    const searchValue = search
      .trim()
      .toLowerCase();

    return [...statements]
      .sort((a, b) => {
        const aValue =
          Number(a.statement_year || 0) * 100 +
          Number(a.statement_month || 0);

        const bValue =
          Number(b.statement_year || 0) * 100 +
          Number(b.statement_month || 0);

        return bValue - aValue;
      })
      .filter((item) => {
        const statementNumber =
          getStatementNumber(item).toLowerCase();

        const month = getMonthName(
          item.statement_month
        ).toLowerCase();

        const statementYear = String(
          item.statement_year || ""
        );

        const matchesSearch =
          !searchValue ||
          statementNumber.includes(searchValue) ||
          month.includes(searchValue) ||
          statementYear.includes(searchValue);

        const matchesYear =
          year === "All" ||
          String(item.statement_year) ===
            String(year);

        const documentStatus = item.file_url
          ? "Available"
          : "Not Available";

        const matchesStatus =
          status === "All" ||
          documentStatus === status;

        return (
          matchesSearch &&
          matchesYear &&
          matchesStatus
        );
      });
  }, [
    statements,
    search,
    year,
    status,
  ]);

  const supplierName =
    supplier?.supplier_name || "Supplier";

  const supplierMobile =
    supplier?.mobile ||
    "Mobile number unavailable";

  const supplierCode =
    supplier?.supplier_code || "—";

  const initial = getInitial(supplierName);

  const handleView = (statement) => {
    if (!statement?.file_url) {
      return;
    }

    let fileUrl = statement.file_url;

    if (
      fileUrl.startsWith("/") &&
      !fileUrl.startsWith("//")
    ) {
      const apiBase =
        process.env.REACT_APP_API_URL ||
        process.env.REACT_APP_API_BASE_URL ||
        "http://127.0.0.1:8000/api";

      const backendOrigin = apiBase.replace(
        /\/api\/?$/,
        ""
      );

      fileUrl = `${backendOrigin}${fileUrl}`;
    }

    window.open(
      fileUrl,
      "_blank",
      "noopener,noreferrer"
    );
  };

  const handleLogout = () => {
    const confirmed = window.confirm(
      "Are you sure you want to logout from the Supplier Portal?"
    );

    if (!confirmed) {
      return;
    }

    supplierPortalService.logout();

    navigate("/supplier/login", {
      replace: true,
    });
  };

  const goDashboard = () => {
    setProfileOpen(false);
    setSidebarOpen(false);

    navigate("/supplier/dashboard");
  };

  const goDashboardSection = (section) => {
    setProfileOpen(false);
    setSidebarOpen(false);

    navigate(
      `/supplier/dashboard?section=${section}`
    );
  };

  const goStatements = () => {
    setProfileOpen(false);
    setSidebarOpen(false);

    navigate("/supplier/statements");
  };

  if (loading) {
    return (
      <div className="supplier-dashboard">
        <div className="supplier-main">
          <main className="supplier-dashboard-content">
            <div className="supplier-statements-loading">
              <div className="supplier-loading-spinner" />
              <p>Loading statements...</p>
            </div>
          </main>
        </div>
      </div>
    );
  }

  return (
    <div className="supplier-dashboard">

      {/* SIDEBAR */}
      <aside
        className={`supplier-sidebar ${
          sidebarOpen ? "open" : ""
        }`}
      >
        <div className="supplier-sidebar-brand">
          <div className="supplier-brand-icon">
            🚌
          </div>

          <div>
            <strong>
              Fleet Management Pro
            </strong>

            <span>
              SUPPLIER PORTAL
            </span>
          </div>
        </div>

        <nav className="supplier-sidebar-nav">

          <button
            type="button"
            className="supplier-nav-item"
            onClick={goDashboard}
          >
            <span className="supplier-nav-icon">
              ⌂
            </span>

            <span>
              Dashboard
            </span>
          </button>

          <button
            type="button"
            className="supplier-nav-item"
            onClick={() =>
              goDashboardSection("vehicles")
            }
          >
            <span className="supplier-nav-icon">
              ▣
            </span>

            <span>
              My Vehicles
            </span>
          </button>

          <button
            type="button"
            className="supplier-nav-item"
            onClick={() =>
              goDashboardSection("documents")
            }
          >
            <span className="supplier-nav-icon">
              ▤
            </span>

            <span>
              Vehicle Documents
            </span>
          </button>

          <button
            type="button"
            className="supplier-nav-item active"
            onClick={goStatements}
          >
            <span className="supplier-nav-icon">
              ▥
            </span>

            <span>
              Statements
            </span>
          </button>

        </nav>
      </aside>

      {/* MOBILE OVERLAY */}
      {sidebarOpen && (
        <button
          type="button"
          aria-label="Close navigation"
          className="supplier-sidebar-overlay"
          onClick={() =>
            setSidebarOpen(false)
          }
        />
      )}

      {/* MAIN */}
      <div className="supplier-main">

        {/* TOPBAR */}
        <header className="supplier-topbar">

          <div className="supplier-topbar-left">

            <button
              type="button"
              className="supplier-menu-btn"
              aria-label="Open navigation"
              onClick={() =>
                setSidebarOpen(
                  (value) => !value
                )
              }
            >
              ☰
            </button>

            <div className="supplier-topbar-title">
              <strong>
                Fleet Management Pro
              </strong>

              <span>
                Supplier Portal
              </span>
            </div>

          </div>

          <div className="supplier-topbar-right">

            {/* PROFILE */}
            <div className="supplier-profile-wrap">

              <button
                type="button"
                className="supplier-profile-btn"
                aria-label="Open supplier profile"
                onClick={() =>
                  setProfileOpen(
                    (value) => !value
                  )
                }
              >
                {initial}
              </button>

              {profileOpen && (
                <div className="supplier-profile-dropdown">

                  <div className="supplier-profile-head">

                    <div className="supplier-dropdown-avatar">
                      {initial}
                    </div>

                    <div>
                      <strong>
                        {supplierName}
                      </strong>

                      <span>
                        Supplier
                      </span>
                    </div>

                  </div>

                  <div className="supplier-profile-divider" />

                  <div className="supplier-profile-info">

                    <span>
                      Mobile
                    </span>

                    <strong>
                      {supplierMobile}
                    </strong>

                  </div>

                  <div className="supplier-profile-info">

                    <span>
                      Supplier Code
                    </span>

                    <strong>
                      {supplierCode}
                    </strong>

                  </div>

                  {supplier?.email && (
                    <div className="supplier-profile-info">

                      <span>
                        Email
                      </span>

                      <strong>
                        {supplier.email}
                      </strong>

                    </div>
                  )}

                  <div className="supplier-profile-divider" />

                  <button
                    type="button"
                    className="supplier-dropdown-logout"
                    onClick={handleLogout}
                  >
                    Logout
                  </button>

                </div>
              )}

            </div>

            <button
              type="button"
              className="supplier-topbar-logout"
              onClick={handleLogout}
            >
              Logout
            </button>

          </div>

        </header>

        {/* CONTENT */}
        <main className="supplier-dashboard-content">

          {errorMessage && (
            <div className="supplier-statements-error">
              {errorMessage}
            </div>
          )}

          <section className="supplier-statements-card">

            {/* TITLE */}
            <div className="supplier-statements-title-row">

              <div>

                <span className="supplier-statements-eyebrow">
                  SUPPLIER PORTAL
                </span>

                <h1>
                  Supplier Statements
                </h1>

                <p>
                  Monthly statements for{" "}
                  <strong>
                    {supplierName}
                  </strong>
                </p>

              </div>

              <button
                type="button"
                className="supplier-dashboard-link"
                onClick={goDashboard}
              >
                Dashboard
              </button>

            </div>

            {/* FILTERS */}
            <div className="supplier-statement-filters">

              <div className="supplier-filter-field">

                <label>
                  Year
                </label>

                <select
                  value={year}
                  onChange={(e) =>
                    setYear(e.target.value)
                  }
                >

                  <option value="All">
                    All Years
                  </option>

                  {years.map(
                    (itemYear) => (
                      <option
                        key={itemYear}
                        value={itemYear}
                      >
                        {itemYear}
                      </option>
                    )
                  )}

                </select>

              </div>

              <div className="supplier-filter-field">

                <label>
                  Document
                </label>

                <select
                  value={status}
                  onChange={(e) =>
                    setStatus(e.target.value)
                  }
                >

                  <option value="All">
                    All Documents
                  </option>

                  <option value="Available">
                    Available
                  </option>

                  <option value="Not Available">
                    Not Available
                  </option>

                </select>

              </div>

              <div className="supplier-filter-field supplier-search-field">

                <label>
                  Search
                </label>

                <div className="supplier-search-box">

                  <span>
                    ⌕
                  </span>

                  <input
                    type="search"
                    placeholder="Search statement..."
                    value={search}
                    onChange={(e) =>
                      setSearch(e.target.value)
                    }
                  />

                </div>

              </div>

            </div>

            {/* SUMMARY */}
            <div className="supplier-statements-summary">

              <div>
                <span>
                  Total Statements
                </span>

                <strong>
                  {statements.length}
                </strong>
              </div>

              <div>
                <span>
                  Showing
                </span>

                <strong>
                  {filteredStatements.length}
                </strong>
              </div>

              <div>
                <span>
                  Supplier Code
                </span>

                <strong>
                  {supplierCode}
                </strong>
              </div>

            </div>

            {/* EMPTY */}
            {filteredStatements.length === 0 ? (

              <div className="supplier-statements-empty">

                <div className="supplier-empty-icon">
                  📄
                </div>

                <h3>
                  No statements found
                </h3>

                <p>
                  No supplier statements are
                  available for the selected
                  filters.
                </p>

              </div>

            ) : (

              <>

                {/* DESKTOP TABLE */}
                <div className="supplier-statements-desktop-table">

                  <div className="supplier-statements-table-wrap">

                    <table className="supplier-statements-table">

                      <thead>

                        <tr>

                          <th>
                            Statement
                          </th>

                          <th>
                            Month
                          </th>

                          <th>
                            Year
                          </th>

                          <th>
                            Document
                          </th>

                          <th>
                            Action
                          </th>

                        </tr>

                      </thead>

                      <tbody>

                        {filteredStatements.map(
                          (item) => (

                            <tr
                              key={
                                item.id ||
                                getStatementNumber(
                                  item
                                )
                              }
                            >

                              <td>

                                <strong>
                                  {getStatementNumber(
                                    item
                                  )}
                                </strong>

                              </td>

                              <td>
                                {getMonthName(
                                  item.statement_month
                                )}
                              </td>

                              <td>
                                {item.statement_year}
                              </td>

                              <td>

                                <span
                                  className={
                                    item.file_url
                                      ? "supplier-paid"
                                      : "supplier-pending"
                                  }
                                >
                                  {item.file_url
                                    ? "Available"
                                    : "Not Available"}
                                </span>

                              </td>

                              <td>

                                <button
                                  type="button"
                                  className="supplier-view-btn"
                                  disabled={
                                    !item.file_url
                                  }
                                  onClick={() =>
                                    handleView(item)
                                  }
                                >
                                  {item.file_url
                                    ? "View"
                                    : "Unavailable"}
                                </button>

                              </td>

                            </tr>

                          )
                        )}

                      </tbody>

                    </table>

                  </div>

                </div>

                {/* MOBILE LIST */}
                <div className="supplier-statements-mobile-list">

                  {filteredStatements.map(
                    (item) => (

                      <article
                        className="supplier-statement-mobile-card"
                        key={
                          item.id ||
                          getStatementNumber(item)
                        }
                      >

                        <div className="supplier-mobile-card-head">

                          <div>

                            <span className="supplier-mobile-card-label">
                              STATEMENT
                            </span>

                            <h3>
                              {getStatementNumber(
                                item
                              )}
                            </h3>

                          </div>

                          <span
                            className={
                              item.file_url
                                ? "supplier-mobile-document-status available"
                                : "supplier-mobile-document-status unavailable"
                            }
                          >
                            {item.file_url
                              ? "AVAILABLE"
                              : "NOT AVAILABLE"}
                          </span>

                        </div>

                        <div className="supplier-mobile-card-divider" />

                        <div className="supplier-mobile-card-details">

                          <div>

                            <span>
                              MONTH
                            </span>

                            <strong>
                              {getMonthName(
                                item.statement_month
                              )}
                            </strong>

                          </div>

                          <div>

                            <span>
                              YEAR
                            </span>

                            <strong>
                              {item.statement_year}
                            </strong>

                          </div>

                        </div>

                        <div className="supplier-mobile-file-row">

                          <div className="supplier-mobile-file-icon">
                            📄
                          </div>

                          <div>

                            <span>
                              DOCUMENT
                            </span>

                            <strong>
                              Supplier Statement
                            </strong>

                          </div>

                        </div>

                        <button
                          type="button"
                          className="supplier-mobile-view-btn"
                          disabled={
                            !item.file_url
                          }
                          onClick={() =>
                            handleView(item)
                          }
                        >

                          <span>
                            ◉
                          </span>

                          {item.file_url
                            ? "View Statement"
                            : "Document Unavailable"}

                        </button>

                      </article>

                    )
                  )}

                </div>

              </>

            )}

            {/* FOOTER */}
            <div className="supplier-statements-footer">

              <span>

                Showing{" "}

                <strong>
                  {filteredStatements.length}
                </strong>{" "}

                of{" "}

                <strong>
                  {statements.length}
                </strong>{" "}

                statements

              </span>

            </div>

          </section>

        </main>

      </div>

    </div>
  );
};

export default SupplierStatements;